/**
 * Product Card Component
 *
 * Self-contained product card with variant selection, quantity controls,
 * and add-to-cart functionality for location-based product purchases.
 *
 * @element peach-product-card
 *
 * @fires add-to-cart - Emitted when user adds product to cart
 *
 * @example
 * ```html
 * <peach-product-card
 *   .product=${product}
 *   .location=${location}
 *   .selectedStop=${stop}
 * ></peach-product-card>
 * ```
 */

import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { cartStore } from "@/features/cart/stores/cart-store";
import { uiStore } from "@/stores/ui-store";
import { ensureHttps } from "@/utils/url";
import type { Location, Stop } from "@/types/location";
import type {
  ShopifyProduct,
  ShopifyProductVariant,
  CartItem,
} from "@/types/shopify";
import "@/features/ui/quantity-input";
import "@/features/ui/card";

@customElement("peach-product-card")
export class ProductCardComponent extends LitElement {
  /**
   * Render in light DOM to enable Tailwind CSS classes
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Product to display
   */
  @property({ type: Object })
  declare product: ShopifyProduct;

  /**
   * Location context (for cart properties)
   */
  @property({ type: Object })
  declare location: Location;

  /**
   * Selected stop (for cart properties)
   */
  @property({ type: Object })
  declare selectedStop: Stop;

  /**
   * Whether this is the last item in the list
   */
  @property({ type: Boolean })
  declare last: boolean;

  /**
   * Whether checkout is disabled for the current stop (preorders closed/overfilled)
   */
  @property({ type: Boolean })
  declare checkoutDisabled: boolean;

  /**
   * Whether this product has walk-up availability at the location
   */
  @property({ type: Boolean })
  declare walkUpAvailable: boolean;

  /**
   * Whether this product has preorder availability at the location
   */
  @property({ type: Boolean })
  declare preorderAvailable: boolean;

  /**
   * Whether ALL products at this location are walk-up only
   */
  @property({ type: Boolean })
  declare allWalkUp: boolean;

  /**
   * Product quantity (internal state)
   */
  @state()
  declare private quantity: number;

  /**
   * Selected variant ID (internal state)
   */
  @state()
  declare private selectedVariantId: string | null;

  /**
   * Loading state for add to cart
   */
  @state()
  declare private isAddingToCart: boolean;

  /**
   * Quantity of this product/variant in cart
   */
  @state()
  declare private cartQuantity: number;

  /**
   * Unsubscribe function for cart store
   */
  private unsubscribeCart?: () => void;

  constructor() {
    super();
    this.last = false;
    this.checkoutDisabled = false;
    this.walkUpAvailable = false;
    this.preorderAvailable = true;
    this.allWalkUp = false;
    this.quantity = 1;
    this.selectedVariantId = null;
    this.isAddingToCart = false;
    this.cartQuantity = 0;
  }

  /**
   * Subscribe to cart changes when component connects
   */
  connectedCallback() {
    super.connectedCallback();

    // Subscribe to cart store to track quantity in cart
    this.unsubscribeCart = cartStore.subscribe((state) => {
      this.updateCartQuantity(state.items);
    });

    // Initial cart quantity calculation
    this.updateCartQuantity(cartStore.getState().items);
  }

  /**
   * Unsubscribe when component disconnects
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribeCart?.();
  }

  /**
   * Calculate how many of this product/variant are in the cart
   * for the CURRENT location and selected stop
   */
  private updateCartQuantity(cartItems: CartItem[]) {
    const selectedVariant = this.selectedVariantId
      ? this.product.variants.find(
        (v) => String(v.id) === this.selectedVariantId,
      )
      : this.product.variants[0];

    if (!selectedVariant) {
      this.cartQuantity = 0;
      return;
    }

    // If no location or stop context, don't show cart quantity
    if (!this.location || !this.selectedStop) {
      this.cartQuantity = 0;
      return;
    }

    // Sum up quantities for this variant + location + stop combination
    const matchingItems = cartItems.filter((item) => {
      // Must match variant ID
      if (String(item.variant_id) !== String(selectedVariant.id)) {
        return false;
      }

      // Must match location (tour ID)
      const itemTourId = item.properties?.["_tour-id"];
      if (String(itemTourId) !== String(this.location.id)) {
        return false;
      }

      // Must match stop (tour date ID)
      const itemTourDateId = item.properties?.["_tour-date-id"];
      if (String(itemTourDateId) !== String(this.selectedStop.id)) {
        return false;
      }

      return true;
    });

    this.cartQuantity = matchingItems.reduce(
      (sum, item) => sum + item.quantity,
      0,
    );
  }

  /**
   * Update selected variant
   */
  private handleVariantChange(variantId: string | number) {
    this.selectedVariantId = String(variantId);
  }

  /**
   * Add product to cart
   */
  private async handleAddToCart() {
    if (!this.location || !this.selectedStop) {
      console.warn(
        "[ProductCard] Cannot add to cart: missing location or stop",
      );
      return;
    }

    const variantId = this.selectedVariantId
      ? Number(this.selectedVariantId)
      : this.product.variants[0]?.id;

    if (!variantId) {
      console.error(
        "[ProductCard] No variant selected for product:",
        this.product.id,
      );
      return;
    }

    this.isAddingToCart = true;

    try {
      // Add line item properties for location context
      const properties = {
        Location: this.location.name,
        "Location Address": this.location.address,
        "Location Date/Time": `${this.selectedStop.date} | ${this.selectedStop.start_time} - ${this.selectedStop.end_time} ${this.selectedStop.timezone}`,
        "_tour-id": String(this.location.id),
        "_tour-date-id": String(this.selectedStop.id),
      };

      await cartStore.getState().addItem(variantId, this.quantity, properties);

      // Open cart after adding
      uiStore.getState().openCart();

      this.dispatchEvent(
        new CustomEvent("add-to-cart", {
          detail: { product: this.product, variantId, quantity: this.quantity },
          bubbles: true,
          composed: true,
        }),
      );
    } catch (error) {
      console.error("[ProductCard] Failed to add to cart:", error);
    } finally {
      this.isAddingToCart = false;
    }
  }

  /**
   * Format price for display
   */
  private formatPrice(cents: number): string {
    return `$${(cents / 100).toFixed(2)}`;
  }


  /**
   * Main render method
   */
  render() {
    const selectedVariant = this.selectedVariantId
      ? this.product.variants.find(
        (v: ShopifyProductVariant) => String(v.id) === this.selectedVariantId,
      )
      : this.product.variants[0];

    const displayPrice = selectedVariant?.price ?? this.product.price;
    const comparePrice = selectedVariant?.compare_at_price;
    const hasMultipleVariants =
      this.product.variants.length > 1 && this.product.options[0] !== "Title";

    return html`
      <peach-card variant="list-item" padding="sm" .last=${this.last} class="overflow-visible">
        <!-- Product Header with Image -->
        <div class="flex gap-3 mb-3">
          <!-- Image container with optional Sold Out badge -->
          <div class="relative flex-shrink-0">
            ${this.product.featured_image
        ? html`
                <img
                  src="${ensureHttps(this.product.featured_image)}"
                  alt="${this.product.title}"
                  class="w-20 h-20 object-cover rounded-lg"
                />
              `
        : html`
                <div
                  class="w-20 h-20 bg-surface-subtle rounded-lg flex items-center justify-center"
                >
                  <svg
                    class="w-8 h-8 text-subtle"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              `
      }
            ${!this.product.available
        ? html`
                <div class="absolute top-0 left-0 bg-neutral-800 text-white text-xs font-semibold px-2 py-1 rounded-t-lg">
                  Sold Out
                </div>
              `
        : ""
      }
          </div>
          <div class="flex-1 min-w-0">
            <h4 class="leading-tight mb-1">
              ${this.product.title}
            </h4>
            ${this.product.short_description
              ? html`<p class="text-sm text-default leading-snug mb-1">${this.product.short_description}</p>`
              : ""
            }
            <div class="flex items-baseline gap-2">
              <p class="text-lg font-bold text-brand">
                ${this.formatPrice(displayPrice)}
              </p>
              ${comparePrice && comparePrice > displayPrice
        ? html`
                    <p class="text-sm text-subtle line-through">
                      ${this.formatPrice(comparePrice)}
                    </p>
                  `
        : ""
      }
            </div>
            ${this.cartQuantity > 0
        ? html`
                  <div class="inline-flex items-center gap-1 px-2 py-0.5 bg-success-soft border border-success rounded-full mt-1">
                    <svg class="w-3 h-3 text-success-icon" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                    </svg>
                    <span class="text-xs text-success-text font-semibold">${this.cartQuantity} in cart</span>
                  </div>
                `
        : ""
      }
          </div>
        </div>

        <!-- Variant Selector (if multiple variants) -->
        ${hasMultipleVariants
        ? html`
              <div class="mb-3">
                <label class="block text-xs font-medium text-default mb-1">
                  ${this.product.options[0]}
                </label>
                <select
                  @change=${(e: Event) =>
            this.handleVariantChange(
              (e.target as HTMLSelectElement).value,
            )}
                  class="w-full px-2 py-1.5 text-sm border border-strong rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                >
                  ${this.product.variants.map(
              (variant: ShopifyProductVariant) => html`
                      <option
                        value="${variant.id}"
                        ?selected=${String(variant.id) === this.selectedVariantId ||
                (!this.selectedVariantId &&
                  variant === this.product.variants[0])
                }
                      >
                        ${variant.title} - ${this.formatPrice(variant.price)}
                      </option>
                    `,
            )}
                </select>
              </div>
            `
        : ""
      }

        <!-- Per-product walkup text (only when not allWalkUp and not checkoutDisabled) -->
        ${this.walkUpAvailable && this.preorderAvailable && !this.checkoutDisabled && !this.allWalkUp
          ? html`
            <div class="flex items-center gap-1.5 mb-2">
              <svg class="w-3.5 h-3.5 text-warning-text flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <span class="text-xs text-warning-text">Walk-ups available while supplies last</span>
            </div>
          `
          : ""
        }

        ${// Add to Cart cascade: walkUpOnly → soldOut → preordersClosed+walkUp → preordersClosed → pickupOnly → noPreorder → button
          this.walkUpAvailable && !this.preorderAvailable
          // Walk-up only: never shows sold out (inventory not tracked)
          ? html`
              <div class="w-full px-3 py-3 bg-warning-soft border-warning-ring rounded-lg">
                <div class="flex items-center justify-center gap-1.5">
                  <peach-icon name="walk-ups" class="text-warning-text" size="sm"></peach-icon>
                  <p class="text-sm text-warning-text font-medium">Walk-ups Only</p>
                </div>
              </div>
            `
          // Sold out: only for products with preorder capability
          : !this.product.available
            ? html`
                <div class="w-full px-3 py-3 bg-peach-100 border-2 border-peach-300 rounded-lg">
                  <div class="flex items-center justify-center gap-1.5">
                    <peach-icon name="sold-out" class="text-peach-600" size="sm"></peach-icon>
                    <p class="text-sm text-peach-700 font-medium">Sold Out</p>
                  </div>
                </div>
              `
            // Preorders closed but walk-ups available
            : this.checkoutDisabled && this.walkUpAvailable
              ? html`
                  <div class="w-full px-3 py-3 bg-warning-soft border-warning-ring rounded-lg">
                    <div class="flex items-center justify-center gap-1.5">
                      <peach-icon name="walk-ups" class="text-warning-text" size="sm"></peach-icon>
                      <p class="text-sm text-warning-text font-medium">Walk-ups Only</p>
                    </div>
                  </div>
                `
              // Preorders closed, no walk-ups
              : this.checkoutDisabled
                ? ""
                // Legacy pickup-only tag
                : this.product.tags?.includes("pickup-only")
                  ? html`
                    <div class="w-full px-3 py-3 bg-warning-soft border-warning-ring rounded-lg">
                      <div class="flex items-center justify-center gap-1.5">
                        <peach-icon name="walk-ups" class="text-warning-text" size="sm"></peach-icon>
                        <p class="text-sm text-warning-text font-medium">Walk-ups Only</p>
                      </div>
                    </div>
                  `
                  // No preorder available
                  : !this.preorderAvailable
                    ? ""
                    // Default: Add to Cart button
                    : html`
                    <div class="flex items-center gap-2 min-h-[44px]">
                      <button
                        @click=${() => this.handleAddToCart()}
                        ?disabled=${this.isAddingToCart || !this.location || !this.selectedStop}
                        class="flex-1 px-4 py-2 text-white text-sm font-semibold rounded-lg transition-colors
                          disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2
                          bg-brand-accent hover:bg-brand-accent-hover"
                      >
                        ${this.isAddingToCart
                          ? html`
                              <div class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>Adding...</span>
                            `
                          : html`
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                              <span>Add to Cart</span>
                            `
                        }
                      </button>
                    </div>
                  `
        }
      </peach-card>
    `;
  }
}

/**
 * TypeScript declaration for JSX/Liquid integration
 */
declare global {
  interface HTMLElementTagNameMap {
    "peach-product-card": ProductCardComponent;
  }
}
